import { useState, useEffect } from "react";
import {
  ContentLayout, Header, Container, Box, Button, Alert, Spinner,
  SpaceBetween, ColumnLayout, ExpandableSection, Tabs,
} from "@cloudscape-design/components";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ResponsiveContainer,
} from "recharts";
import { boxerService } from "../../services/boxerService";
import MarkdownRenderer from "../../components/common/MarkdownRenderer";
import DataTable from "../../components/common/DataTable";

// ── Reusable section wrapper with its own spinner ────────────────
function Section({ loading, error, children, minHeight = 80,  }) {
  if (loading) return (
    <Box textAlign="center" padding="l" color="text-body-secondary">
      <Spinner size="normal" /> &nbsp; Loading...
    </Box>
  );
  if (error) return <Alert type="error">{error}</Alert>;
  return children;
}

// ── SectionBlock: AI insight + DataTable ────────────────────────
function SectionBlock({ table, aiInsight, emptyMsg, columnHighlights, title }) {
  const columns = table && table.length > 0 ? Object.keys(table[0]) : [];
  return (
    <SpaceBetween size="m">
      {aiInsight && aiInsight.trim() && (
        <div style={sStyle.insight}>
          <Box fontWeight="bold" padding={{ bottom: "xs" }}>🔍 AI Insight</Box>
          <MarkdownRenderer text={aiInsight.trim()} />
        </div>
      )}
      {table && table.length > 0
        ? <DataTable columns={columns} data={table} columnHighlights={columnHighlights || {}} title={title} rowCount={table.length} />
        : <Box color="text-body-secondary" textAlign="center" padding="l">{emptyMsg}</Box>
      }
    </SpaceBetween>
  );
}

const sStyle = {
  insight: { background: "#f8f9fb", border: "1px solid #d5dbdb", borderRadius: 8, padding: "14px 18px" },
};

// ── Per-section state helper ─────────────────────────────────────
const initSection = () => ({ data: null, loading: true, error: "" });

export default function DeliveryCommandCenter() {
  // Each section has independent loading/data/error state
  const [snapshot,    setSnapshot]    = useState(initSection());
  const [aiInsight,   setAiInsight]   = useState(initSection());
  const [liveNetwork, setLiveNetwork] = useState(initSection());
  const [unshipped,   setUnshipped]   = useState(initSection());
  const [atRisk,      setAtRisk]      = useState(initSection());

  const fetchSection = async (fetcher, setter) => {
    setter(s => ({ ...s, loading: true, error: "" }));
    try {
      const data = await fetcher();
      setter({ data, loading: false, error: "" });
    } catch (err) {
      setter({ data: null, loading: false, error: err.response?.data?.detail || "Failed to load." });
    }
  };

  const loadAll = () => {
    // Fire all 5 requests in parallel — each section renders as soon as its own request completes
    fetchSection(() => boxerService.getCCSnapshot(),    setSnapshot);
    fetchSection(() => boxerService.getCCAiInsight(),   setAiInsight);
    fetchSection(() => boxerService.getCCLiveNetwork(), setLiveNetwork);
    fetchSection(() => boxerService.getCCUnshipped(),   setUnshipped);
    fetchSection(() => boxerService.getCCAtRisk(),      setAtRisk);
  };

  useEffect(() => { loadAll(); }, []);

  // Derived values from snapshot section
  const kpis   = snapshot.data?.kpis   || [];
  const target = snapshot.data?.target || 95;

  const withData    = kpis.filter(k => k.total_orders > 0);
  const totalOrders = withData.reduce((s, k) => s + (Number(k.total_orders) || 0), 0);
  const onTime      = withData.reduce((s, k) => s + (Number(k.orders_shipped_on_time) || 0), 0);
  const late        = withData.reduce((s, k) => s + (Number(k.late_orders) || 0), 0);
  const avgPct      = totalOrders > 0 ? ((onTime / totalOrders) * 100).toFixed(1) : "—";
  const statusColor = avgPct === "—" ? "#666" : parseFloat(avgPct) >= target ? "#1d8102" : parseFloat(avgPct) > 92 ? "#df6a00" : "#d13212";

  const chartMap = {};
  kpis.forEach(k => {
    const d = k.earliest_ship_date;
    if (!d) return;
    if (!chartMap[d]) chartMap[d] = { date: d, total: 0, count: 0 };
    chartMap[d].total += Number(k.shipment_pct) || 0;
    chartMap[d].count += 1;
  });
  const chartData = Object.values(chartMap)
    .sort((a, b) => a.date.localeCompare(b.date))
    .map(d => ({ date: d.date, "On-Time %": parseFloat((d.total / d.count).toFixed(1)) }));

  const liveNetworkHighlights = {
    "Live Adherence (%)":          "#0eb922",
    "Projected EOD Adherence (%)": "#4688df",
    "Total Unshipped Orders":      "#fa4c4c",
  };

  const kpiColumns = kpis.length > 0 ? Object.keys(kpis[0]) : [];

  return (
    <ContentLayout
      header={
        <Header variant="h1"
          description="% of active shipments projected to be shipped on time"
          actions={<Button iconName="refresh" onClick={loadAll}>Refresh Data</Button>}
        >
          🚚 Live Delivery Command Center
        </Header>
      }
    >
      <SpaceBetween size="l">

        {/* Overview */}
        <Container>
          <ExpandableSection headerText="ℹ️ Overview">
            <SpaceBetween size="m">
              <Box>
                The <strong>Live Delivery Adherence Command Center</strong> provides a real-time,
                network-wide view of shipment performance across all Distribution Centers (DCs).
                <br/>
                It automatically connects to the Boxer S3 bucket, ingests operational CSV data, extracts shipping timelines, 
                and calculates adherence metrics needed for rapid decision-making.
              </Box>
              <Box>
                <strong>A real-time operational view of all network shipments by analyzing:</strong> 
                <br/>
                <ul>
                  <li>Earliest, latest, and actual ship dates for every order</li>
                  <li>Active shipments that still matter for today</li>
                  <li>At-Risk and Critical shipments that may miss SLA</li>
                  <li>DC-level performance breakdowns</li>
                  <li>Live adherence metrics and SLA tracking</li>
                  <li>Insights highlighting the biggest risks and required actions</li>
                  <li>An Executive Snapshot summarizing today’s operational health</li>
                </ul>
              </Box>
              <ColumnLayout columns={3} variant="text-grid">
                <div>
                  <Box variant="awsui-key-label">🎯 Live Target</Box>
                  <Box>🟩 {target}% Adherence</Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">Status Indicators</Box>
                  <Box>✅ On Track &nbsp; ⚠️ At Risk &nbsp; 🚨 Critical</Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">Trend vs Target</Box>
                  <Box>⬈ Above &nbsp; ➜ Stable &nbsp; ⬊ Falling</Box>
                </div>
              </ColumnLayout>
              <line />
              <Box>
                <h2>📘 Summary of the Performance</h2>
                Network-wide, DC-wide, Store-wide shipment performance and identified the following:
                <br/>
                <h3>1. Live Warehouse Performance Summary</h3>
                <ul>
                  <li>Provides a Live, DC-Level Shipment Performance Snapshot</li>
                  <li>Tracks Today’s Operational Load & Delay Pressure</li>
                  <li>Measures Execution Quality with Live Adherence Metrics</li>
                  <li>Predicts End-of-Day Performance (Projected EOD Adherence)</li>
                  <li>Executive Insights for Fast Decision Making</li>
                </ul>
                <h3>2. Unshipped Order Summary</h3>
                <ul>
                  <li>Monitors All Unshipped Orders in Real Time</li>
                  <li>Breaks Unshipped Work Into 5 Operational Buckets</li>
                  <li>Highlights Total Unshipped Workload per DC</li>
                  <li>Surfaces Immediate Risk for Tomorrow & Day-After Shipments</li>
                  <li>Executive-Ready Insights for Leadership</li>
                </ul>
                <h3>3. Unshipped Orders by Store</h3>
                <ul>
                  <li>Provides a Live View of Unshipped Orders by Store + DC</li>
                  <li>Categorizes Unshipped Orders Into 5 Operational Buckets</li>
                  <li>Highlights Total Unshipped Orders per Store Region</li>
                  <li>Identifies Store-Level Future Risks (Tomorrow & Day After)</li>
                  <li>Store-Specific AI Leadership Insights</li>
                </ul>
                <h3>4. Live 'At-Risk' Shipments Ticker</h3>
                <ul>
                  <li>Monitors Live At-Risk Shipments</li>
                  <li>Filters High-Risk Orders</li>
                  <li>Identifies Risk Drivers</li>
                  <li>Executive Insights for immediate action</li>
                </ul>
              </Box>
            </SpaceBetween>
          </ExpandableSection>
        </Container>

        {/* ── Section 1: Executive Snapshot ── loads first */}
        <Container header={<Header variant="h2">📋 Executive Snapshot: The 'Right Now' Status</Header>}>
          <Section loading={snapshot.loading} error={snapshot.error}>
            {snapshot.data?.executive_snapshot?.trim()
              ? <MarkdownRenderer text={snapshot.data.executive_snapshot.trim()} />
              : <Box color="text-body-secondary">Executive snapshot data not available.</Box>
            }
          </Section>
        </Container>

        {/* ── Section 2: AI Insight ── loads independently (LLM call) */}
        {/* {(!aiInsight.loading && aiInsight.data?.ai_insight?.trim()) && (
          <Container header={<Header variant="h2">AI Insight</Header>}>
            <Section loading={aiInsight.loading} error={aiInsight.error}>
              <MarkdownRenderer text={aiInsight.data.ai_insight.trim()} />
            </Section>
          </Container>
        )}
        {aiInsight.loading && (
          <Container header={<Header variant="h2">AI Insight</Header>}>
            <Box textAlign="center" padding="l" color="text-body-secondary">
              <Spinner size="normal" /> &nbsp; Generating AI insight...
            </Box>
          </Container>
        )} */}

        {/* ── Section 3: Chart ── renders from snapshot data */}
        {/* {chartData.length > 0 && (
          <Container header={<Header variant="h2">📈 On-Time Shipment % Over Time</Header>}>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" angle={-45} textAnchor="end" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} />
                <Tooltip formatter={v => [`${v}%`, "On-Time %"]} />
                <ReferenceLine y={target} stroke="#e07b39" strokeDasharray="6 3"
                  label={{ value: `Target ${target}%`, position: "insideTopRight", fill: "#e07b39", fontSize: 11 }} />
                <Line type="monotone" dataKey="On-Time %" stroke="#0073bb" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </Container>
        )} */}

        {/* ── Section 4: Live Network ── independent */}
        <Container header={<Header variant="h2">🏭 Live Warehouse Performance Summary</Header>}>
          <SpaceBetween size="m">
            <ExpandableSection headerText="ℹ️ Additional Detail">
              <SpaceBetween size="s">
                <Box><h2>📘 Key Calculations</h2></Box>
                <Box><strong>Active order filter:</strong> <code>latest_ship_date ≤ today</code> AND (<code>actual_ship_date</code> NULL within last 5 days OR shipped today).</Box>
                <Box><strong>Total Orders:</strong> Count of all active orders</Box>
                <Box><strong>Shipped Orders:</strong> <code>actual_ship_date</code> NOT NULL</Box>
                <Box><strong>On-Time Orders:</strong> <code>actual_ship_date ≤ latest_ship_date</code></Box>
                <Box><strong>Unshipped Orders:</strong> <code>actual_ship_date</code> IS NULL</Box>
                <Box><strong>Late Shipped Orders:</strong> <code>1 ≤ (actual_ship_date − latest_ship_date) ≤ 3</code></Box>
                <Box><strong>Live Adherence(%):</strong> <code>(On-Time Orders / Total Shipped Orders) × 100</code></Box>
                <Box><strong>Projected EOD:</strong> Forecasts end-of-day performance by combining shipped + unshipped orders.
                  <ol type="1">
                    <li> Forecast adherence for unshipped orders
                      <ul>
                        <li>Uses Exponential Smoothing (α = 0.3) on historical adherence.</li>
                        <li>Applies a risk adjustment factor based on current unshipped volume.</li>
                      </ul>
                    </li>
                    <li> EOD Projection Formula
                      <ul>
                        <li><code>Projected EOD Adherence (%) =((On-Time Shipped) + (Unshipped × Forecast Adherence/100))/ Total Orders × 100</code></li>
                      </ul>
                    </li>
                  </ol>
                </Box>
              </SpaceBetween>
            </ExpandableSection>
            <Section loading={liveNetwork.loading} error={liveNetwork.error}>
              <SectionBlock
                table={liveNetwork.data?.table}
                aiInsight={liveNetwork.data?.ai_insight}
                emptyMsg="No live network order data available."
                columnHighlights={liveNetworkHighlights}
                title="Live Network Orders"
              />
            </Section>
          </SpaceBetween>
        </Container>

        {/* ── Section 5 & 6: Unshipped + At-Risk tabs ── each independent */}
        <Tabs
          tabs={[
            {
              label: "📦 Unshipped Orders",
              id: "unshipped",
              content: (
                <SpaceBetween size="m">
                  <ExpandableSection headerText="ℹ️ Additional Detail">
                    <SpaceBetween size="s">
                        <Box><h2>📘 Key Calculations</h2></Box>
                        <Box>This dashboard tracks all unshipped orders as of today and groups them by when they were supposed to ship.</Box>
                        <Box>Only orders with <strong>no Actual Ship Date (Unshipped)</strong> are included</Box>
                        <Box><strong>Unshipped Orders (Past Days):</strong> <code>latest_ship_date is between (today − 6) and (today − 1)</code></Box>
                        <Box><strong>Unshipped Orders (Yesterday):</strong> <code>latest_ship_date = today − 1</code></Box>
                        <Box><strong>Unshipped Orders (Today):</strong> <code>latest_ship_date = today</code></Box>
                        <Box><strong>Orders Due Tomorrow:</strong> <code>latest_ship_date = today + 1</code></Box>
                        <Box><strong>Orders Due Day After Tomorrow:</strong> <code>latest_ship_date = today + 2</code></Box>
                        <Box><strong>Purpose:</strong>
                          <ul>
                            <li>Identify backlogged unshipped orders.</li>
                            <li>Highlight yesterday & today failures.</li>
                            <li>Provide early view of tomorrow/day-after workload.</li>
                            <li>Help DCs take corrective actions before issues accumulate.</li>
                          </ul>
                        </Box>
                      </SpaceBetween>
                  </ExpandableSection>
                  <Section loading={unshipped.loading} error={unshipped.error}>
                    <SectionBlock
                      table={unshipped.data?.table}
                      aiInsight={unshipped.data?.ai_insight}
                      emptyMsg="No unshipped order data available."
                      title="Unshipped Orders"
                    />
                    {unshipped.data?.store_table?.length > 0 && (
                      <ExpandableSection headerText="🏬 Unshipped Orders by Store">
                        <SectionBlock
                          table={unshipped.data.store_table}
                          aiInsight={unshipped.data.store_ai_insight}
                          emptyMsg="No store-level data available."
                          title="Unshipped by Store"
                        />
                      </ExpandableSection>
                    )}
                  </Section>
                </SpaceBetween>
              ),
            },
            {
              label: "📢 At-Risk Shipments",
              id: "atrisk",
              content: (
                <SpaceBetween size="m">
                  <ExpandableSection headerText="ℹ️ Additional Detail">
                    <SpaceBetween size="s">
                        <Box><h2>📘 Key Calculations</h2></Box>
                        <Box>This module identifies live at-risk shipments using the same activity filter as the Live Network Dashboard.</Box>
                        <Box>Only <strong>shipments with Latest Ship Date ≤ Today</strong> are considered active. Unshipped shipments are only included if they fall within the last 4 days to ensure real-time relevance.</Box>
                        <Box><strong>Active Shipments:</strong> <code>latest_ship_date ≤ today AND (actual_ship_date IS NULL AND (today - latest_ship_date) ≤ 4 days OR actual_ship_date greater than latest_ship_date (already late))</code></Box>
                        <Box><strong>Late Shipped:</strong> <code>actual_ship_date greater than latest_ship_date</code></Box>
                        <Box><strong>Overdue & Not Shipped:</strong> <code>actual_ship_date IS NULL AND today greater than latest_ship_date</code> Risk Score = 0.90</Box>
                        <Box><strong>Due Today & Not Shipped:</strong> <code>actual_ship_date IS NULL AND today == latest_ship_date</code></Box>
                        <Box><strong>Purpose:</strong>
                          <ul>
                            <li>Identify live SLA failures and upcoming risks</li>
                            <li>Pinpoint DCs driving late trends</li>
                            <li>Support real-time escalation</li>
                            <li>Help avoid same-day SLA breaches by acting early</li>
                          </ul>
                        </Box>
                      </SpaceBetween>
                  </ExpandableSection>
                  <Section loading={atRisk.loading} error={atRisk.error}>
                    <SectionBlock
                      table={atRisk.data?.table}
                      aiInsight={atRisk.data?.ai_insight}
                      emptyMsg="No at-risk shipments detected."
                      title="At-Risk Shipments"
                    />
                  </Section>
                </SpaceBetween>
              ),
            },
          ]}
        />

        {/* KPI Detail */}
        {/* {kpis.length > 0 && (
          <ExpandableSection headerText={`KPI Detail Table (${kpis.length} records)`}>
            <DataTable columns={kpiColumns} data={kpis.slice(0, 200)} title="KPI Details" />
          </ExpandableSection>
        )} */}

        <Alert type="info">
          🎯 <strong>100% Real Data Analysis</strong> — All metrics and projections are based exclusively on S3 CSV data with no fabricated information.
        </Alert>

      </SpaceBetween>
    </ContentLayout>
  );
}